import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { ChevronRight } from 'lucide-react';

import { apiGetAsUser, apiPatchAsUser } from '../../lib/api_v2';
import db from '../../lib/db';
import { getDesktopBridge, startDesktopOauth } from '../../lib/desktopOauth';
import { recordDesktopNavigationIntent } from '../../lib/desktop_navigation_diagnostics';
import { logError } from '../../lib/logger';
import { getMetaContent, rootDomainUrl } from '../../lib/meta';
import { logoutOneSignal } from '../../lib/onesignal';
import { cn } from '../../lib/utils';

import ContactBookImage from '../../../assets/images/contact-book.png';
import GoogleLogoIcon from '../../../assets/images/google-logo.svg';

import useDesktopWindowFocusSignal from '../../hooks/useDesktopWindowFocusSignal';
import useModalSections from '../../hooks/useModalSections';
import FlexiconEditor from '../FlexiconEditor';
import AccountAdvancedPanel from '../settings/account/AccountAdvancedPanel';
import AccountEmailPanel from '../settings/account/AccountEmailPanel';
import AccountPasswordPanel from '../settings/account/AccountPasswordPanel';
import ConnectedServicesPanel from '../settings/account/ConnectedServicesPanel';
import { firstFieldError, responseValidations } from '../settings/account/accountStyles';
import SettingsBackButton from '../shared/SettingsBackButton';
import StatusTag from '../shared/StatusTag';
import {
  SETTINGS_MODAL_CLOSE_BUTTON_CLASS,
  SETTINGS_MODAL_CLOSE_ICON,
  SETTINGS_MODAL_SAVE_PILL_CLASS,
  SETTINGS_MODAL_TITLE_CLASS,
} from '../shared/settingsModalChrome';
import { Modal } from './Modal';
import { ModalBody } from './ModalBody';
import { ModalContent } from './ModalContent';
import { ModalTopBar } from './ModalTopBar';

// Unlink this browser's push subscription before the session ends;
// timeout-raced inside logoutOneSignal so logout can never hang on it.
const handleLogoutSubmit = (event) => {
  const form = event.currentTarget;
  event.preventDefault();
  logoutOneSignal().finally(() => form.submit());
};

const SECTION = {
  ROOT: 'root',
  LOGIN: 'login',
  ADVANCED: 'advanced',
  CONNECTED_SERVICES: 'connected_services',
};
const SECTION_TITLE = {
  [SECTION.LOGIN]: 'Email & Password',
  [SECTION.ADVANCED]: 'Advanced',
  [SECTION.CONNECTED_SERVICES]: 'Connected Services',
};

// Matches MainLayout's variant check; a local copy because importing MainLayout
// from a modal it renders would be a circular import.
const NATIVE_MACOS_APP_VARIANT = 'native-macos';
const MODAL_SECTION_RESET_DELAY_MS = 200;

// Canonical settings card (same recipe as the Members / people-picker cards).
const ACCOUNT_OPTIONS_GROUP_CLASS =
  'flex w-full flex-col gap-4 rounded-lg border-[0.5px] border-white bg-gray-50 p-4 dark:border-neutral-800 dark:bg-neutral-900';
const ACCOUNT_CARD_DIVIDER_CLASS = 'h-[0.5px] w-full bg-gray-200 dark:bg-neutral-800';
const ACCOUNT_CARD_TITLE_CLASS = 'text-sm leading-5 font-medium text-black dark:text-neutral-100';
const ACCOUNT_CARD_SUBTITLE_CLASS = 'text-xs leading-[18px] text-gray-400 dark:text-neutral-500';
const ACCOUNT_CARD_ROW_TEXT_CLASS = 'min-w-0 truncate text-[13px] leading-5 text-black dark:text-neutral-100';
// Same pill as MembersPanel's row actions (the Figma "Contact components" pill).
const ACCOUNT_PILL_BUTTON_CLASS =
  'flex h-9 shrink-0 cursor-pointer items-center justify-center rounded-full border-[0.5px] border-white bg-gray-200 px-3 text-cursed text-black hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100 dark:hover:bg-neutral-700';

const updateCachedOwnedProfileFullNames = async (updatedUser, currentProfile) => {
  const ownedProfileIds = new Set(Array.isArray(updatedUser?.whoami) ? updatedUser.whoami.map(String) : []);
  if (currentProfile?.id) ownedProfileIds.add(String(currentProfile.id));
  if (ownedProfileIds.size === 0) return;

  await db.profiles.filter((profile) => ownedProfileIds.has(String(profile.id))).modify({ full_name: updatedUser.full_name });
};

const AccountOptionButton = ({ children, onClick, danger, type = 'button' }) => {
  return (
    <button
      type={type}
      data-app-region="none"
      onClick={onClick}
      className={cn(
        'flex w-full cursor-pointer items-center justify-between gap-3 text-left text-cursed font-normal',
        danger
          ? 'text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300'
          : 'text-black hover:text-gray-600 dark:text-neutral-100 dark:hover:text-neutral-300',
      )}
    >
      <span className="min-w-0 flex-1 truncate">{children}</span>
    </button>
  );
};

const CONTACT_SYNC_POLL_INTERVAL_MS = 2000;
const CONTACT_SYNC_POLL_MAX_ATTEMPTS = 15;

const currentContactsReturnUrl = () => {
  if (typeof window === 'undefined') return '';

  // The marker makes MainLayout reopen this modal when the OAuth redirect returns.
  const url = new URL(window.location.href);
  url.searchParams.set('account_options', '1');
  return url.toString();
};

const GoogleContactsImportRow = () => {
  const csrfToken = getMetaContent('csrf-token') || '';
  const [returnTo] = useState(currentContactsReturnUrl);
  const [googleSync, setGoogleSync] = useState(null);
  const [isAuthorizing, setIsAuthorizing] = useState(false);
  const awaitingDesktopReturnRef = useRef(false);
  const desktopImportBaselineIdRef = useRef(null);
  const desktopBridge = getDesktopBridge();
  const desktopFocusSignal = useDesktopWindowFocusSignal();
  const importing = Boolean(googleSync?.importing);

  const refreshGoogleSync = useCallback(async function refreshGoogleContactSyncStatus() {
    try {
      const response = await apiGetAsUser('/contact_sync');
      const data = await response.json();
      if (response.ok && data.ok) {
        const googleStatus = data.data?.google || null;
        setGoogleSync(googleStatus);
        return googleStatus;
      }
    } catch (error) {
      logError('AccountOptionsModal', 'Failed to load Google contact sync status', error);
    }

    return null;
  }, []);

  useEffect(
    function loadGoogleContactSyncStatus() {
      refreshGoogleSync();
    },
    [refreshGoogleSync],
  );

  const handleDesktopImport = useCallback(
    async function handleDesktopGoogleContactsImport() {
      if (!desktopBridge || isAuthorizing) return;

      setIsAuthorizing(true);

      try {
        const currentGoogleStatus = await refreshGoogleSync();
        if (!currentGoogleStatus) {
          setIsAuthorizing(false);
          return;
        }

        desktopImportBaselineIdRef.current = currentGoogleStatus.latest_import_id || null;
        const desktopOauth = await startDesktopOauth({ purpose: 'contacts', provider: 'google' });
        if (desktopOauth?.completed) {
          await refreshGoogleSync();
          desktopImportBaselineIdRef.current = null;
          setIsAuthorizing(false);
        } else if (desktopOauth) {
          awaitingDesktopReturnRef.current = true;
        } else {
          setIsAuthorizing(false);
        }
      } catch (error) {
        logError('AccountOptionsModal', 'Failed to start desktop Google contacts import', error);
        setIsAuthorizing(false);
      }
    },
    [desktopBridge, isAuthorizing, refreshGoogleSync],
  );

  // Covers the web flow too: after the OAuth redirect lands back here, the fresh mount
  // sees importing=true from the API and polls until the import settles.
  useEffect(
    function pollWhileImporting() {
      if (!importing) return undefined;

      const timer = window.setInterval(refreshGoogleSync, CONTACT_SYNC_POLL_INTERVAL_MS);
      return function pollWhileImportingCleanup() {
        window.clearInterval(timer);
      };
    },
    [importing, refreshGoogleSync],
  );

  useEffect(
    function pollContactSyncAfterDesktopReturn() {
      if (!desktopFocusSignal || !awaitingDesktopReturnRef.current) return undefined;

      // Focus can return before the external-browser consent finishes; poll until the
      // server reports the import running (importing then drives the UI) or give up.
      let attempts = 0;
      let timer = null;

      async function pollForImportStart() {
        attempts += 1;
        const googleStatus = await refreshGoogleSync();
        const newImportCreated = googleStatus?.latest_import_id && googleStatus.latest_import_id !== desktopImportBaselineIdRef.current;

        if (googleStatus?.importing || newImportCreated || attempts >= CONTACT_SYNC_POLL_MAX_ATTEMPTS) {
          window.clearInterval(timer);
          awaitingDesktopReturnRef.current = false;
          desktopImportBaselineIdRef.current = null;
          setIsAuthorizing(false);
        }
      }

      timer = window.setInterval(pollForImportStart, CONTACT_SYNC_POLL_INTERVAL_MS);
      pollForImportStart();

      return function pollContactSyncAfterDesktopReturnCleanup() {
        window.clearInterval(timer);
      };
    },
    [desktopFocusSignal, refreshGoogleSync],
  );

  const hasImported = Boolean(googleSync?.enabled);
  const busy = isAuthorizing || importing;
  const buttonLabel = hasImported ? 'Re-sync' : 'Import';

  return (
    <div className="flex w-full items-center justify-between gap-3">
      <div className="flex min-w-0 items-center gap-2">
        <GoogleLogoIcon className="size-9 shrink-0" />
        <span className={ACCOUNT_CARD_ROW_TEXT_CLASS}>Google Contacts</span>
      </div>
      {importing ? (
        <span className="flex flex-none items-center gap-2 px-3 py-2 text-sm text-gray-500 dark:text-neutral-400">
          <span
            className="size-4 animate-spin rounded-full border-2 border-gray-300 border-t-transparent dark:border-neutral-600 dark:border-t-transparent"
            aria-hidden="true"
          />
          Importing...
        </span>
      ) : desktopBridge ? (
        <button type="button" className={ACCOUNT_PILL_BUTTON_CLASS} disabled={busy} onClick={handleDesktopImport}>
          {isAuthorizing ? 'Waiting for Google...' : buttonLabel}
        </button>
      ) : (
        <form method="post" action={rootDomainUrl('/contacts/setup/google')} className="flex-none">
          <input type="hidden" name="authenticity_token" value={csrfToken} readOnly />
          <input type="hidden" name="return_to" value={returnTo} readOnly />
          <button type="submit" className={ACCOUNT_PILL_BUTTON_CLASS} disabled={busy}>
            {buttonLabel}
          </button>
        </form>
      )}
    </div>
  );
};

// "Linked accounts" card: the primary (sign-in) email tagged PRIMARY, plus any
// other linked provider emails as plain rows. Accounts that sign in with email +
// password get a chevron on the primary row into the Email & Password page;
// Google-auth accounts don't manage credentials at all.
const LinkedAccountsCard = ({ providers, userEmail, canManageLogin = false, onOpenLogin }) => {
  const providerEmails = useMemo(
    function collectLinkedProviderEmails() {
      const emails = Array.isArray(providers) ? providers.map((provider) => provider?.email).filter(Boolean) : [];
      return [...new Set(emails)].filter((email) => email !== userEmail);
    },
    [providers, userEmail],
  );

  const rows = [];
  if (userEmail) {
    rows.push({
      key: `primary:${userEmail}`,
      content: canManageLogin ? (
        <button type="button" onClick={onOpenLogin} className="flex w-full cursor-pointer items-center gap-2 text-left">
          <span className={ACCOUNT_CARD_ROW_TEXT_CLASS}>{userEmail}</span>
          <StatusTag variant="blue">PRIMARY</StatusTag>
          <span className="flex-1" />
          <ChevronRight className="size-4.5 shrink-0 text-black dark:text-neutral-100" strokeWidth={1} absoluteStrokeWidth />
        </button>
      ) : (
        <div className="flex w-full items-center gap-2">
          <span className={ACCOUNT_CARD_ROW_TEXT_CLASS}>{userEmail}</span>
          <StatusTag variant="blue">PRIMARY</StatusTag>
        </div>
      ),
    });
  }
  providerEmails.forEach((email) => {
    rows.push({
      key: `linked:${email}`,
      content: (
        <div className="flex w-full items-center gap-2">
          <span className={ACCOUNT_CARD_ROW_TEXT_CLASS}>{email}</span>
        </div>
      ),
    });
  });
  if (!rows.length) return null;

  return (
    <div className={ACCOUNT_OPTIONS_GROUP_CLASS}>
      <div className="flex w-full flex-col gap-1">
        <h3 className={ACCOUNT_CARD_TITLE_CLASS}>Linked accounts</h3>
        <p className={ACCOUNT_CARD_SUBTITLE_CLASS}>All Idle communications will be sent to your primary account</p>
      </div>
      {rows.map((row) => (
        <React.Fragment key={row.key}>
          <div className={ACCOUNT_CARD_DIVIDER_CLASS} />
          {row.content}
        </React.Fragment>
      ))}
    </div>
  );
};

// "Phone Number" card, only when the account has a verified phone identity.
const PhoneNumberCard = ({ phoneNumber }) => {
  if (!phoneNumber) return null;

  return (
    <div className={ACCOUNT_OPTIONS_GROUP_CLASS}>
      <h3 className={ACCOUNT_CARD_TITLE_CLASS}>Phone Number</h3>
      <div className={ACCOUNT_CARD_DIVIDER_CLASS} />
      <div className="flex w-full items-center gap-2">
        <span className={ACCOUNT_CARD_ROW_TEXT_CLASS}>{phoneNumber}</span>
        <StatusTag variant="green">VERIFIED</StatusTag>
      </div>
    </div>
  );
};

const AccountFullNameForm = ({ user, currentProfile }) => {
  const userFullName = user?.full_name || '';
  const userId = user?.id || null;
  const [fullName, setFullName] = useState(userFullName);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(
    function syncFullNameFromUser() {
      setFullName(userFullName);
    },
    [userFullName, userId],
  );

  const handleFullNameChange = useCallback(function handleFullNameChange(event) {
    setFullName(event.target.value);
    setErrors((previousErrors) => (previousErrors.full_name ? { ...previousErrors, full_name: null } : previousErrors));
  }, []);

  const handleFullNameSubmit = useCallback(
    async function handleFullNameSubmit(event) {
      event.preventDefault();
      if (!user || isSaving) return;

      setIsSaving(true);
      setErrors({});

      try {
        const response = await apiPatchAsUser('/me', { user: { full_name: fullName } });
        const data = await response.json();

        if (response.ok && data.ok) {
          const updatedUser = data.data.user;
          await db.users.update(String(updatedUser.id), updatedUser);
          await updateCachedOwnedProfileFullNames(updatedUser, currentProfile);
        } else {
          const validationErrors = responseValidations(data);
          setErrors(validationErrors || { general: 'Failed to save full name.' });
        }
      } catch (error) {
        logError('AccountOptionsModal', 'Failed to save full name:', error);
        setErrors({ general: 'Failed to save full name. Please try again.' });
      } finally {
        setIsSaving(false);
      }
    },
    [currentProfile, fullName, isSaving, user],
  );

  if (!user) return null;

  const fullNameError = firstFieldError(errors.full_name);
  const hasChanges = fullName.trim().length > 0 && fullName.trim() !== userFullName.trim();

  return (
    <form onSubmit={handleFullNameSubmit} className="w-full">
      {/* Floats at the top bar line, mirroring Create Group's Create pill;
          anchored to the Modal root, so it stays put while content scrolls. */}
      <button
        type="submit"
        disabled={isSaving || !hasChanges}
        className={SETTINGS_MODAL_SAVE_PILL_CLASS}
      >
        {isSaving ? 'Saving' : 'Save'}
      </button>
      {errors.general && (
        <div className="mb-3 rounded-md bg-red-50 p-3 text-sm text-red-800 dark:bg-red-950 dark:text-red-200">{errors.general}</div>
      )}
      <input
        type="text"
        id="account_full_name"
        name="full_name"
        value={fullName}
        onChange={handleFullNameChange}
        required
        disabled={isSaving}
        aria-label="Full name"
        data-1p-ignore="true"
        className={cn(
          'h-12 w-full rounded-lg border sm:border-[0.5px] border-white bg-gray-50 px-4 text-[15px] leading-5 text-black outline-none hover:bg-white focus:border-gray-400 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-100',
          fullNameError && 'border-red-300 focus:border-red-400',
        )}
      />
      {fullNameError ? <div className="mt-1 text-xs leading-5 font-medium text-red-600">{fullNameError}</div> : null}
    </form>
  );
};

// "Sync Contacts" card: Contact Book only surfaces in the native macOS app (no
// web backend for Apple contacts yet); Google reuses the existing import row.
const SyncContactsCard = () => {
  const isNativeMacApp = getMetaContent('app:variant') === NATIVE_MACOS_APP_VARIANT;

  return (
    <div className={ACCOUNT_OPTIONS_GROUP_CLASS}>
      <div className="flex w-full flex-col gap-1">
        <h3 className={ACCOUNT_CARD_TITLE_CLASS}>Sync Contacts</h3>
        <p className={ACCOUNT_CARD_SUBTITLE_CLASS}>Connect your contacts so we can find friends that may already be on Idle.</p>
      </div>
      {isNativeMacApp ? (
        <>
          <div className={ACCOUNT_CARD_DIVIDER_CLASS} />
          <div className="flex w-full items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2">
              <span className="flex size-9 shrink-0 items-center justify-center">
                <img src={ContactBookImage} alt="" className="size-[29px] object-contain" draggable="false" />
              </span>
              <span className={ACCOUNT_CARD_ROW_TEXT_CLASS}>Contact Book</span>
            </div>
            <button type="button" className={ACCOUNT_PILL_BUTTON_CLASS} disabled>
              Sync contacts
            </button>
          </div>
        </>
      ) : null}
      <div className={ACCOUNT_CARD_DIVIDER_CLASS} />
      <GoogleContactsImportRow />
    </div>
  );
};

const AccountOptionsModal = ({ open, onClose, userEmail, user, currentProfile }) => {
  const { section, isNested, goToSection, goBack } = useModalSections({
    open,
    initialSection: SECTION.ROOT,
    resetDelayMs: MODAL_SECTION_RESET_DELAY_MS,
  });
  const accountInitialSource = user?.full_name || userEmail || 'Account';
  const accountInitial = accountInitialSource.trim().charAt(0).toUpperCase();
  const modalContentRef = useRef(null);
  const [connectedServicesNotice, setConnectedServicesNotice] = useState(null);
  const csrfToken = getMetaContent('csrf-token');
  const authProviders = user?.auth_providers;
  // Google-auth accounts never manage email/password (they sign in with Google);
  // the credentials page is only for accounts that sign in via email + password.
  const hasGoogleProvider = Array.isArray(authProviders) && authProviders.some((provider) => provider?.provider === 'google');
  const canManageLogin = !hasGoogleProvider;
  const handleOpenLoginSection = useCallback(
    function openLoginSection() {
      goToSection(SECTION.LOGIN);
    },
    [goToSection],
  );
  const handleOpenAdvancedSection = useCallback(
    function openAdvancedSection() {
      goToSection(SECTION.ADVANCED);
    },
    [goToSection],
  );
  const handleOpenConnectedServices = useCallback(() => {
    setConnectedServicesNotice(null);
    goToSection(SECTION.CONNECTED_SERVICES);
  }, [goToSection]);

  useLayoutEffect(() => {
    if (!open) return;
    const url = new URL(window.location.href);
    if (url.searchParams.get('account_options_section') !== SECTION.CONNECTED_SERVICES) return;
    setConnectedServicesNotice({
      provider: url.searchParams.get('connection_provider'),
      status: url.searchParams.get('connection_status'),
      connectionId: url.searchParams.get('connection_id'),
    });
    url.searchParams.delete('account_options_section');
    url.searchParams.delete('connection_provider');
    url.searchParams.delete('connection_status');
    url.searchParams.delete('connection_id');
    window.history.replaceState(window.history.state, '', url.toString());
    goToSection(SECTION.CONNECTED_SERVICES);
  }, [goToSection, open]);

  const handleLogoutSubmit = useCallback(function handleLogoutSubmitCallback(event) {
    recordDesktopNavigationIntent({ action: 'navigate', reason: 'logout', targetUrl: event.currentTarget.action });
  }, []);

  useEffect(() => {
    if (!modalContentRef.current) return;
    modalContentRef.current.scrollTop = 0;
  }, [section]);

  return (
    <Modal
      onClose={onClose}
      className="rounded-2xl"
      closePlacement="top-left"
      showClose={{ desktop: !isNested, mobile: !isNested }}
      closeButtonClassName={SETTINGS_MODAL_CLOSE_BUTTON_CLASS}
      closeIcon={SETTINGS_MODAL_CLOSE_ICON}
    >
      {isNested && <SettingsBackButton onClick={goBack} />}
      <ModalBody className="h-full">
        <ModalTopBar className="!pb-0">
          <span className={SETTINGS_MODAL_TITLE_CLASS}>
            {isNested ? SECTION_TITLE[section] || null : 'Your Profile'}
          </span>
        </ModalTopBar>
        <ModalContent ref={modalContentRef} className={cn('min-h-0 md:!px-5', section === SECTION.ROOT && 'flex items-start justify-center')}>
          {section === SECTION.ROOT && (
            <div className="flex w-full flex-col pt-6 pb-2">
              <div className="flex w-full justify-center">
                {user ? (
                  <FlexiconEditor
                    resource={user}
                    resourceType="user"
                    layout="stacked"
                    actionsPresentation="overlay"
                    previewSize="size-30"
                    previewRounded="rounded-full"
                  />
                ) : (
                  <div className="flex size-30 items-center justify-center rounded-full bg-gray-100 text-4xl font-semibold text-gray-700 dark:bg-neutral-800 dark:text-neutral-200">
                    {accountInitial}
                  </div>
                )}
              </div>

              <div className="mt-8 flex w-full flex-col gap-2">
                <label htmlFor="account_full_name" className="text-[13px] leading-5 text-black dark:text-neutral-100">
                  Name
                </label>
                <AccountFullNameForm user={user} currentProfile={currentProfile} />
              </div>

              <div className="mt-5 flex w-full flex-col gap-5">
                <PhoneNumberCard phoneNumber={user?.verified_phone_number} />
                <LinkedAccountsCard
                  providers={authProviders}
                  userEmail={userEmail}
                  canManageLogin={canManageLogin}
                  onOpenLogin={handleOpenLoginSection}
                />
                <SyncContactsCard />
                <div className={ACCOUNT_OPTIONS_GROUP_CLASS}>
                  <AccountOptionButton onClick={handleOpenConnectedServices}>Connected services</AccountOptionButton>
                </div>
                <div className={ACCOUNT_OPTIONS_GROUP_CLASS}>
                  <AccountOptionButton onClick={handleOpenAdvancedSection}>Advanced</AccountOptionButton>
                </div>
                <form method="post" action="/logout" className={ACCOUNT_OPTIONS_GROUP_CLASS} onSubmit={handleLogoutSubmit}>
                  <input type="hidden" name="_method" value="delete" />
                  {csrfToken && <input type="hidden" name="authenticity_token" value={csrfToken} />}
                  <AccountOptionButton type="submit" danger>
                    Log out
                  </AccountOptionButton>
                </form>
              </div>
            </div>
          )}

          {section === SECTION.LOGIN && (
            <div className="flex w-full flex-col gap-8 pt-6">
              <AccountEmailPanel userEmail={userEmail} />
              {/* Page-level divider: the card divider's gray-200 vanishes on the
                  gray-200 modal background. */}
              <div className="h-[0.5px] w-full bg-gray-300/60 dark:bg-white/10" />
              <AccountPasswordPanel />
            </div>
          )}
          {section === SECTION.ADVANCED && <AccountAdvancedPanel />}
          {section === SECTION.CONNECTED_SERVICES && <ConnectedServicesPanel initialNotice={connectedServicesNotice} />}
        </ModalContent>
      </ModalBody>
    </Modal>
  );
};

export default AccountOptionsModal;
